import { useTheme } from "@/components/theme-provider";
import { BCFViewpointsPlugin } from "@xeokit/xeokit-sdk";
import { Perspective } from "@xeokit/xeokit-sdk/types/viewer/scene/camera/Perspective";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ToggleInvisibleAsXray,
    addHighlightedEntities,
    addInvisibleEntities,
    addSelectedEntities,
    addXrayedEntities,
    removeHighlightedEntities,
    removeInvisibleEntities,
    removeSelectedEntity,
    removeXrayedEntities,
    setGridVisibility,
    setHighlightedEntities,
    setInvisibleEntities,
    setOpenPlugin,
    setProjection,
    setSectionPlanes,
    setSelectedEntities,
    setSpaceVisibility,
    setXrayedEntities,
    toggleAddingSectionPlane,
} from "../../Context/ViewerContext/Reducer";
import { ViewerContext } from "../../Context/ViewerContext/ViewerContextProvider";
import { setModels } from "../../Context/XktContext/Reducer";
import { XktContext } from "../../Context/XktContext/XktContextProvider";
import { BcfGetOptions, BcfSetOptions, CameraData, SectionPlaneParams } from "../../Models/DstudioViewer";
import Model from "../../Models/Model";
import useFlyTo from "../FlyTo/FlyTo";

const useGlobalDstudioViewer = () => {
    const { state: viewerState, dispatch: viewerDispatch, pendingSectionCreate } = useContext(ViewerContext);
    const { state, dispatch } = useContext(XktContext);
    const { flyTo } = useFlyTo();
    const [bcfViewpointsPlugin, setBcfViewpointsPlugin] = useState<BCFViewpointsPlugin | null>(null);
    const { i18n } = useTranslation();
    const { theme, setTheme } = useTheme();

    useEffect(() => {
        if (!state.viewer) return;

        setBcfViewpointsPlugin(new BCFViewpointsPlugin(state.viewer));
    }, [state.viewer]);

    useEffect(() => {
        if (!state.viewer) return;

        window.bimcradle = {
            version: "1.0.0",
            mode: theme,
            setMode: (theme: "light" | "dark" | "system") => {
                setTheme(theme);
            },
            selection: {
                get: () => {
                    return viewerState.selectedEntities as string[];
                },
                set: (entities: string[]) => {
                    viewerDispatch(setSelectedEntities(entities));
                },
                add: (entity: string) => {
                    viewerDispatch(addSelectedEntities([entity]));
                },
                remove: (entity: string) => {
                    viewerDispatch(removeSelectedEntity([entity]));
                },
                clear: () => {
                    viewerDispatch(setSelectedEntities([]));
                },
            },
            highlight: {
                get: () => {
                    return viewerState.highlightedEntities as string[];
                },
                set: (entities: string[]) => {
                    viewerDispatch(setHighlightedEntities(entities));
                },
                add: (entity: string) => {
                    viewerDispatch(addHighlightedEntities([entity]));
                },
                remove: (entity: string) => {
                    viewerDispatch(removeHighlightedEntities([entity]));
                },
                clear: () => {
                    viewerDispatch(setHighlightedEntities([]));
                },
            },
            hidden: {
                get: () => {
                    return viewerState.invisibleEntities as string[];
                },
                set: (entities: string[]) => {
                    viewerDispatch(setInvisibleEntities(entities));
                },
                add: (entity: string) => {
                    viewerDispatch(addInvisibleEntities([entity]));
                },
                remove: (entity: string) => {
                    viewerDispatch(removeInvisibleEntities([entity]));
                },
                clear: () => {
                    viewerDispatch(setInvisibleEntities([]));
                },
            },
            xray: {
                get: () => {
                    return viewerState.xrayedEntities as string[];
                },
                set: (entities: string[]) => {
                    viewerDispatch(setXrayedEntities(entities));
                },
                add: (entity: string) => {
                    viewerDispatch(addXrayedEntities([entity]));
                },
                remove: (entity: string) => {
                    viewerDispatch(removeXrayedEntities([entity]));
                },
                clear: () => {
                    viewerDispatch(setXrayedEntities([]));
                },
            },
            coloring: {
                get: () => {
                    if (!state.viewer) return {};
                    const colors: { [key: string]: number[] } = {};
                    for (const ent of Object.keys(state.viewer.scene.objects)) {
                        const col = state.viewer.scene.objects[ent].colorize;
                        if (col) colors[ent] = [col[0], col[1], col[2]];
                    }

                    return colors;
                },
                set: (entities: string | string[], color: number[]) => {
                    if (!state.viewer) return;

                    const entitiesArray = typeof entities === "string" ? [entities] : entities;
                    for (const entity of entitiesArray) {
                        state.viewer.scene.setObjectsColorized([entity], color);
                    }
                },
                add: (entities: string | string[], color: number[]): void => {
                    if (!state.viewer) return;

                    const entitiesArray = typeof entities === "string" ? [entities] : entities;
                    for (const entity of entitiesArray) {
                        state.viewer.scene.setObjectsColorized([entity], color);
                    }
                },
                remove: (entities: string | string[]): void => {
                    if (!state.viewer) return;

                    const entitiesArray = typeof entities === "string" ? [entities] : entities;
                    for (const entity of entitiesArray) {
                        // @ts-expect-error : colorize accepts null value to remove colorization, Xeokit types not complete
                        state.viewer.scene.objects[entity].colorize = null;
                    }
                },
                clear: (): void => {
                    if (!state.viewer) return;

                    for (const ent of Object.keys(state.viewer.scene.objects)) {
                        // @ts-expect-error : colorize accepts null value to remove colorization, Xeokit types not complete
                        state.viewer.scene.objects[ent].colorize = null;
                    }
                },
            },
            opacity: {
                set: (entities: string | string[], opacity: number) => {
                    if (!state.viewer) return;

                    const entitiesArray = typeof entities === "string" ? [entities] : entities;
                    state.viewer.scene.setObjectsOpacity(entitiesArray, opacity);
                },
                remove: (entities: string | string[]) => {
                    if (!state.viewer) return;

                    const entitiesArray = typeof entities === "string" ? [entities] : entities;
                    state.viewer.scene.setObjectsOpacity(entitiesArray, 1);
                },
                clear: () => {
                    if (!state.viewer) return;

                    const allEntities = Object.keys(state.viewer.scene.objects || {}) as string[];
                    state.viewer.scene.setObjectsOpacity(allEntities, 1);
                },
            },
            elements: {
                get: () => {
                    return Object.keys(state.viewer?.scene.objects || {}) as string[];
                },
            },

            loadModels: (paths: Model[]) => {
                dispatch(setModels(paths));
            },
            loadModel: (path: Model) => {
                const models = [...state.models];
                models.push(path);
                dispatch(setModels(models));
            },
            unloadModel: (modelId: string) => {
                const models = [...state.models.filter((x) => x.modelId !== modelId)];
                dispatch(setModels(models));
            },
            camera: {
                getMode: () => {
                    if (!state.viewer) return "";
                    return state.viewer.camera.projection;
                },
                setMode: (mode: "perspective" | "ortho" | "frustum") => {
                    if (!state.viewer) return;
                    state.viewer.camera.projection = mode;
                    viewerDispatch(setProjection(mode));
                },
                getPosition: (): CameraData => {
                    const cameraData = {
                        eye: [0, 0, 0],
                        look: [0, 0, 0],
                        up: [0, 0, 0],
                        fov: 0,
                        fovAxis: "",
                        near: 0,
                        far: 0,
                        projection: "perspective",
                    } as CameraData;
                    if (!state.viewer) return cameraData;
                    const cam = state.viewer.scene.camera;
                    const project = cam.project as Perspective;
                    cameraData.eye[0] = cam.eye[0];
                    cameraData.eye[1] = cam.eye[1];
                    cameraData.eye[2] = cam.eye[2];
                    cameraData.look[0] = cam.look[0];
                    cameraData.look[1] = cam.look[1];
                    cameraData.look[2] = cam.look[2];
                    cameraData.up[0] = cam.up[0];
                    cameraData.up[1] = cam.up[1];
                    cameraData.up[2] = cam.up[2];
                    cameraData.projection = cam.projection;
                    cameraData.fov = project.fov;
                    cameraData.fovAxis = project.fovAxis;
                    cameraData.near = project.near;
                    cameraData.far = project.far;
                    return cameraData;
                },
                setPosition: (cameraData: CameraData) => {
                    if (!state.viewer) return;
                    const camera = state.viewer.scene.camera;
                    camera.eye[0] = cameraData.eye[0];
                    camera.eye[1] = cameraData.eye[1];
                    camera.eye[2] = cameraData.eye[2];
                    camera.look[0] = cameraData.look[0];
                    camera.look[1] = cameraData.look[1];
                    camera.look[2] = cameraData.look[2];
                    camera.up[0] = cameraData.up[0];
                    camera.up[1] = cameraData.up[1];
                    camera.up[2] = cameraData.up[2];
                    camera.perspective.fov = cameraData.fov;
                    camera.perspective.fovAxis = cameraData.fovAxis;
                    camera.perspective.near = cameraData.near;
                    camera.perspective.far = cameraData.far;
                    camera.projection = cameraData.projection == "perspective" ? "ortho" : "perspective";
                    viewerDispatch(setProjection(camera.projection));
                    camera.projection = cameraData.projection;
                },
                flyTo: (entities: string[]) => {
                    if (!state.viewer) return;
                    flyTo(entities);
                },
            },
            sections: {
                get: (): SectionPlaneParams[] => {
                    if (!viewerState.sectionPlanesPlugin) return [];
                    const sections = Object.values(viewerState.sectionPlanesPlugin.sectionPlanes);
                    const enabledControlId = viewerState.sectionPlanesPlugin.getShownControl();
                    return sections.map((s) => {
                        const sectionplane = {
                            id: s.id,
                            pos: [...s.pos],
                            dir: [...s.dir],
                            active: s.active,
                            controlEnabled: s.id === enabledControlId,
                        } as SectionPlaneParams;
                        return sectionplane;
                    });
                },

                create: (params: SectionPlaneParams) => {
                    if (!viewerState.sectionPlanesPlugin) return;
                    if (!state.viewer) return;
                    if (typeof params === "string") {
                        params = JSON.parse(params);
                    }

                    const { aabb } = state.viewer.scene;
                    const aabbCenter = [
                        (aabb[3] - aabb[0]) / 2 + aabb[0],
                        (aabb[4] - aabb[1]) / 2 + aabb[1],
                        (aabb[5] - aabb[2]) / 2 + aabb[2],
                    ];

                    params.pos = params.pos || aabbCenter;

                    // const sectionPlane = new SectionPlane(state.viewer.scene, {
                    //     ...params,
                    // });
                    const sectionPlane = viewerState.sectionPlanesPlugin.createSectionPlane({
                        ...params,
                    });
                    viewerState.sectionPlanesPlugin.showControl(sectionPlane.id);

                    viewerDispatch(setSectionPlanes([...viewerState.sectionPlanes, sectionPlane]));
                    if (params.enableControls !== false) viewerState.sectionPlanesPlugin.showControl(sectionPlane.id);

                    return sectionPlane.id;
                },

                createInteractive: (): Promise<string> => {
                    if (!viewerState.sectionPlanesPlugin) return Promise.reject("No sectionPlanesPlugin");
                    if (!state.viewer) return Promise.reject("No viewer");

                    return new Promise((resolve, reject) => {
                        pendingSectionCreate.current = { resolve, reject };
                        viewerDispatch(setOpenPlugin("SectionPlanes"));
                        viewerDispatch(toggleAddingSectionPlane(true));
                    });
                },

                toggle: (id: string) => {
                    if (!viewerState.sectionPlanesPlugin) return;
                    const section = viewerState.sectionPlanesPlugin.sectionPlanes[id];
                    if (!section) {
                        return;
                    }

                    section.active = !section.active;
                },

                remove: (id: string) => {
                    if (!viewerState.sectionPlanesPlugin) return;
                    viewerState.sectionPlanesPlugin.destroySectionPlane(id);
                    viewerDispatch(setSectionPlanes([...viewerState.sectionPlanes.filter((s) => s.id !== id)]));
                },
                getActiveControl: (): string => {
                    if (!viewerState.sectionPlanesPlugin) return "";
                    return viewerState.sectionPlanesPlugin.getShownControl();
                },
                setActiveControl: (id: string) => {
                    if (!viewerState.sectionPlanesPlugin) return;
                    const activeControlId = viewerState.sectionPlanesPlugin.getShownControl();
                    viewerState.sectionPlanesPlugin.hideControl();

                    if (id && activeControlId !== id) {
                        viewerState.sectionPlanesPlugin.showControl(id);
                    }
                },
            },
            settings: {
                setInvisibleAsXray: function (invisibleAsXray: boolean): void {
                    viewerDispatch(ToggleInvisibleAsXray(invisibleAsXray));
                },
                getInvisibleAsXray: function (): boolean {
                    return viewerState.invisibleAsXray;
                },
                setSpaceVisibility: function (spaceVisibility: boolean): void {
                    viewerDispatch(setSpaceVisibility(spaceVisibility));
                },
                getSpaceVisibility: function (): boolean {
                    return viewerState.spaceEnabled;
                },
                setGridVisibility: function (gridVisibility: boolean): void {
                    viewerDispatch(setGridVisibility(gridVisibility));
                },
                getGridVisibility: function (): boolean {
                    return viewerState.gridEnabled;
                },
                setLanguage: function (language: string): void {
                    i18n.changeLanguage(language);
                },
                getLanguage: function (): string {
                    return i18n.language;
                },
            },
            bcf: {
                getViewpoint: (options?: BcfGetOptions) => {
                    if (!bcfViewpointsPlugin) return null;
                    const bcfValue = bcfViewpointsPlugin.getViewpoint(options);
                    const myInterval = setInterval(() => {
                        const removeCanvases = Array.from(document.querySelectorAll("canvas")).filter(
                            (x) => x.id === ""
                        );
                        if (removeCanvases.length > 0) {
                            removeCanvases.forEach((c) => {
                                c.remove();
                            });
                            clearInterval(myInterval);
                        }
                    }, 50);
                    return bcfValue;
                },
                setViewpoint: (bcfData: string, options?: BcfSetOptions) => {
                    if (!bcfViewpointsPlugin) return null;
                    bcfViewpointsPlugin.setViewpoint(JSON.parse(bcfData), options);
                },
            },
        };

        return () => {
            window.bimcradle = undefined;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.viewer,
        state.models,
        viewerState.selectedEntities,
        viewerState.highlightedEntities,
        viewerState.invisibleEntities,
        viewerState.xrayedEntities,
    ]);
};

export default useGlobalDstudioViewer;
